import { Controller } from '@hotwired/stimulus'
import type EditorJS from '@editorjs/editorjs'
import type { I18nDictionary, OutputData, ToolConstructable } from '@editorjs/editorjs'

// English mirror of the RU_MESSAGES dictionary used on the russia branch —
// Editor.js's toolbar/tunes chrome is English by default, so this is a
// no-op, but keeping the i18n wiring symmetric across branches means only
// the dictionary content differs, not the config shape.
const EN_MESSAGES: I18nDictionary = {
  ui: {
    blockTunes: {
      toggler: {
        'Click to tune': 'Click to tune',
        'or drag to move': 'or drag to move',
      },
    },
    inlineToolbar: {
      converter: {
        'Convert to': 'Convert to',
      },
    },
    toolbar: {
      toolbox: {
        Add: 'Add',
      },
    },
    popover: {
      Filter: 'Filter',
      'Nothing found': 'Nothing found',
    },
  },
  toolNames: {
    Text: 'Text',
    Heading: 'Heading',
    List: 'List',
    Quote: 'Quote',
    Delimiter: 'Delimiter',
    Table: 'Table',
  },
  tools: {
    link: {
      'Add a link': 'Add a link',
    },
    stub: {
      'The block can not be displayed correctly.': 'The block can not be displayed correctly.',
    },
    list: {
      Ordered: 'Ordered',
      Unordered: 'Unordered',
    },
    quote: {
      'Align Left': 'Align Left',
      'Align Center': 'Align Center',
    },
    table: {
      'Add column to left': 'Add column to left',
      'Add column to right': 'Add column to right',
      'Delete column': 'Delete column',
      'Add row above': 'Add row above',
      'Add row below': 'Add row below',
      'Delete row': 'Delete row',
      'With headings': 'With headings',
      'Without headings': 'Without headings',
      'Add row': 'Add row',
      'Add column': 'Add column',
    },
  },
  blockTunes: {
    delete: {
      Delete: 'Delete',
      'Click to delete': 'Click to delete',
    },
    moveUp: {
      'Move up': 'Move up',
    },
    moveDown: {
      'Move down': 'Move down',
    },
  },
}

// Mounts an Editor.js block editor for one locale of a long-form field. The
// initial document is read from a hidden input (JSON) and the serialized document
// is written back to it on every change, so a normal form submit carries the
// latest content.
//
// Init is deferred until the holder actually scrolls into view (IntersectionObserver).
// This keeps a page with many editors (e.g. the landing editor's repeatable blocks)
// from mounting them all at once, and naturally skips fields in a hidden locale tab
// (display:none → not intersecting) until that tab is shown.
//
// Editor.js and its tools are imported dynamically so they are code-split into a
// chunk that only loads on admin pages that actually mount an editor — the public
// bundle stays small.
export default class extends Controller<HTMLElement> {
  static targets = ['holder', 'input']

  declare readonly holderTarget: HTMLElement
  declare readonly inputTarget: HTMLInputElement

  private editor?: EditorJS
  private observer?: IntersectionObserver

  connect(): void {
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.observer?.disconnect()
        void this.initEditor()
      }
    })
    this.observer.observe(this.element)
  }

  disconnect(): void {
    this.observer?.disconnect()
    this.editor?.destroy?.()
    this.editor = undefined
  }

  private async initEditor(): Promise<void> {
    if (this.editor) return

    const [
      { default: EditorJSCtor },
      { default: Header },
      { default: List },
      { default: Quote },
      { default: Delimiter },
      { default: Table },
    ] = await Promise.all([
      import('@editorjs/editorjs'),
      import('@editorjs/header'),
      import('@editorjs/list'),
      import('@editorjs/quote'),
      import('@editorjs/delimiter'),
      import('@editorjs/table'),
    ])

    // Another init may have completed while we awaited the imports.
    if (this.editor) return

    this.editor = new EditorJSCtor({
      holder: this.holderTarget,
      data: this.initialData,
      minHeight: 120,
      i18n: { messages: EN_MESSAGES },
      tools: {
        header: { class: Header, config: { levels: [2, 3], defaultLevel: 2 } },
        list: { class: List, inlineToolbar: true },
        quote: Quote,
        delimiter: Delimiter,
        // @editorjs/table's published types mark `config` as required instead of
        // optional, so its class doesn't structurally satisfy ToolConstructable.
        table: { class: Table as unknown as ToolConstructable, inlineToolbar: true, config: { rows: 2, cols: 2, withHeadings: false } },
      },
      onChange: async () => {
        const output = await this.editor!.save()
        this.inputTarget.value = JSON.stringify(output)
      },
    })
  }

  private get initialData(): OutputData | undefined {
    const raw = this.inputTarget.value.trim()
    if (!raw) return undefined
    try {
      return JSON.parse(raw) as OutputData
    } catch {
      return undefined
    }
  }
}
